/**
 * Authentication Context
 * Provides auth state and methods to all components
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import api from '../services/apiService';

// Create context
export const AuthContext = createContext(null);

/**
 * Helper function to load and apply user's theme preference
 */
const loadUserTheme = async () => {
  try {
    const response = await api.get('/settings/theme');
    if (response.data.success && response.data.theme) {
      localStorage.setItem('theme', response.data.theme);
      document.body.setAttribute('data-theme', response.data.theme);
      console.log('Applied user theme:', response.data.theme);
    }
  } catch (error) {
    console.log('Could not load theme preference, using localStorage');
    // Fall back to localStorage theme
    const savedTheme = localStorage.getItem('theme') || 'gold';
    document.body.setAttribute('data-theme', savedTheme);
  }
};

/**
 * Auth Provider Component
 * Wraps the app and provides auth state/methods
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have a stored token
        if (authService.isAuthenticated()) {
          // Validate token and get user info
          const isValid = await authService.validateToken();

          if (isValid) {
            const userData = await authService.getCurrentUser();
            setUser(userData);

            // Load user's theme preference after restoring session
            await loadUserTheme();
          } else {
            // Token invalid, clear it
            authService.removeToken();
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        authService.removeToken();
      } finally {
        setInitializing(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (email, password, rememberMe = true) => {
    setError(null);
    setLoading(true);

    try {
      const data = await authService.login(email, password, rememberMe);

      // Get full user profile
      const userData = await authService.getCurrentUser();
      setUser(userData);

      // Load user's theme preference after successful login
      await loadUserTheme();

      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Register new user
   */
  const register = useCallback(async (email, password, name, height_cm) => {
    setError(null);
    setLoading(true);

    try {
      const data = await authService.register(email, password, name, height_cm);

      // Registration includes auto-login
      if (data.user) {
        setUser(data.user);
      }

      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    setLoading(true);

    try {
      await authService.logout();
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request password reset
   */
  const forgotPassword = useCallback(async (email) => {
    setError(null);

    try {
      const data = await authService.forgotPassword(email);
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Reset password with token
   */
  const resetPassword = useCallback(async (token, newPassword) => {
    setError(null);

    try {
      const data = await authService.resetPassword(token, newPassword);
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (profileData) => {
    setError(null);

    try {
      const data = await authService.updateProfile(profileData);
      setUser(data);
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context value
  const value = {
    user,
    initializing,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
